import { Check, Loader2 } from "lucide-react";
import { PlanQueries } from "@/components/research/ResearchProgress";
import { plural, stagesFor } from "@/lib/research";
import { cn } from "@/lib/utils";

const LABELS = { preparing: "Starting", planning: "Planning searches", searching: "Searching the web", analyzing: "Reading full pages" };

// Every detail shown here comes from a real pipeline event (plan, per-query search results, page reads).
function detailFor(key, live) {
  if (key === "planning" && live.plan?.length) return `${plural(live.plan.length, "search")} planned`;
  if (key === "searching" && live.searches) {
    const s = live.searches;
    return `${s.done} of ${plural(s.total, "search")} done · ${plural(s.results, "result")} found${live.sources?.length ? ` · ${plural(live.sources.length, "source")} kept` : ""}`;
  }
  if (key === "analyzing" && live.reads) {
    const r = live.reads;
    return r.done ? `Read ${r.read} of ${plural(r.total, "page")}${r.failed ? ` · ${r.failed} couldn't be read` : ""}` : `Opening the top ${plural(r.total, "page")}`;
  }
  return null;
}

function StepIcon({ done, active }) {
  return (
    <span className={cn("mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors duration-200", done ? "border-white/20 bg-white text-ink-950" : active ? "border-white/40 text-white" : "border-white/10 text-zinc-600")}>
      {done ? <Check className="h-3 w-3" strokeWidth={3} /> : active ? <Loader2 className="h-3 w-3 animate-spin" /> : <span className="h-1 w-1 rounded-full bg-current" />}
    </span>
  );
}

export function SearchProgress({ depth, live }) {
  const stages = stagesFor(depth, live.plan, live.stage);
  const current = stages.findIndex((s) => s.key === live.stage);
  return (
    <ol className="space-y-3" data-testid="search-progress" data-stage={live.stage}>
      {stages.map((s, i) => {
        const done = live.stage === "done" || i < current;
        const active = i === current;
        const label = s.key === "writing" ? (depth === "deep" ? "Writing the report" : "Writing the answer") : LABELS[s.key];
        const detail = done || active ? detailFor(s.key, live) : null;
        return (
          <li key={s.key} className="flex gap-2.5" data-testid={`search-step-${s.key}`} data-state={done ? "done" : active ? "active" : "pending"}>
            <StepIcon done={done} active={active} />
            <div className="min-w-0 flex-1 space-y-1.5">
              <div className={cn("text-[13px] leading-5", done ? "text-zinc-400" : active ? "text-white" : "text-zinc-600")}>{label}</div>
              {detail && <div className="text-[12px] text-zinc-500" data-testid={`search-step-${s.key}-detail`}>{detail}</div>}
              {s.key === "planning" && (done || active) && <PlanQueries queries={live.plan} />}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
